import { Injectable } from '@angular/core';
import { MatDateFormats, NativeDateAdapter } from '@angular/material/core';

export const customDateFormats: MatDateFormats = {
    parse: {
        dateInput: { dateStyle: 'medium' },

        timeInput: { hour: 'numeric', minute: '2-digit' },
    },
    display: {
        dateInput: { dateStyle: 'medium' },
        monthYearLabel: { year: 'numeric', month: 'short' },
        dateA11yLabel: { dateStyle: 'medium' },
        monthYearA11yLabel: { year: 'numeric', month: 'long' },

        timeInput: { hour: '2-digit', minute: '2-digit' },
        timeOptionLabel: { hour: '2-digit', minute: '2-digit' },
    },
};

// https://stackoverflow.com/a/58133271/11271411
@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
    private static dottedDateRegex = /^(?<day>[1-9]|0[1-9]|[12][0-9]|3[01])\.(?<month>[1-9]|0[1-9]|1[0,1,2])\.(?<year>\d{2}|\d{4})$/; // allows date formats with dot as separator; day and month must have one or two digts (leading zeros for values <10 allowed), year must have two or four digits; if year has two digits, 2000 years are added to make it four digits; 1.1.2001 -> 01.01.2001, 31.03.84 -> 31.03.2084
    private static unseparatedDateRegex = /^(?<day>0[1-9]|[12][0-9]|3[01])(?<month>0[1-9]|1[0,1,2])(?<year>\d{4})$/; // allows date formats without separators in ddMMyyyy format; day and month must have two digits (leading zeros for values <10 mandatory), year must have four digits; 01012001 -> 01.01.2001, 13120314 -> 13.12.0314

    private static separatedTimeRegex = /^(?<hour>0?[0-9]|1[0-9]|2[0123])[\.:](?<minute>[0-5][0-9])$/; // allows time fotmats such as 07:00, 5:13 or 14:16, optionally with dot as separator; leading zeros are allowed, minutes must have two digits, hours must have one or two digits and anly hours between 0 and 23 (both inclusive) are allowed; for example, 24:00, 7:4 and 014:45 are not allowed
    private static unseparatedTimeRegex = /^(?<hour>0?[0-9]|1[0-9]|2[0123])(?<minute>[0-5][0-9])$/; // allows time formats without separator: 1605 -> 16:05, 735 -> 07:35, 0735 - 07:35; must have three or four digits and the last two digits are always interpretated as minutes

    override parse(value: unknown): Date | null {
        if (!value) {
            return null;
        }

        if (typeof value !== 'string') {
            return this.invalid();
        }

        return this.#parseDateFromString(value);
    }

    override parseTime(value: unknown): Date | null {
        if (!value) {
            return null;
        }

        if (typeof value !== 'string') {
            return this.invalid();
        }

        return this.#parseTimeFromString(value);
    }

    #parseDateFromString(value: string): Date {
        const { day: _day, month: _month, year: _year } = this.#getDateStringRegExpExecArray(value.trim())?.groups || {};

        const day = Number(_day);
        const month = Number(_month) - 1;
        const year = Number(_year) + Number(Number(_year) < 100) * 2000;

        return this.#parseDateFromParts(year, month, day);
    }

    #getDateStringRegExpExecArray(value: string): RegExpExecArray | null {
        if (CustomDateAdapter.dottedDateRegex.test(value)) {
            return CustomDateAdapter.dottedDateRegex.exec(value);
        }

        if (CustomDateAdapter.unseparatedDateRegex.test(value)) {
            return CustomDateAdapter.unseparatedDateRegex.exec(value);
        }

        return null;
    }

    #parseDateFromParts(year: number, month: number, day: number): Date {
        const date = new Date(year, month, day);

        if (!(date.getFullYear() === year && date.getMonth() === month)) {
            return this.invalid();
        }

        return date;
    }

    #parseTimeFromString(value: string): Date {
        const { hour: _hour, minute: _minute } = this.#getTimeStringRegExpExecArray(value.trim())?.groups || {};

        const hour = Number(_hour);
        const minute = Number(_minute);

        return this.#parseTimeFromParts(hour, minute);
    }

    #getTimeStringRegExpExecArray(value: string): RegExpExecArray | null {
        if (CustomDateAdapter.separatedTimeRegex.test(value)) {
            return CustomDateAdapter.separatedTimeRegex.exec(value);
        }

        if (CustomDateAdapter.unseparatedTimeRegex.test(value)) {
            return CustomDateAdapter.unseparatedTimeRegex.exec(value);
        }

        return null;
    }

    #parseTimeFromParts(hour: number, minute: number): Date {
        const now = new Date();
        const date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute);

        return date;
    }
}
